/**
 * State setters for the User Journey page.
 *
 * The page predates this file and sets state inline; only the payroll
 * connection state moved here, where the keyed-slice bookkeeping actually
 * earns a named setter. Mirrors the idiom in `reports.delegate.js`: plain
 * class, no state of its own, object-spread copies through `this.setState`.
 */
class UserDelegate {
    /**
     * Replaces the connection summary slice.
     * @param {{ loading?: boolean, error?: Object | null, data?: Array<Object> | null }} patch
     */
    setPayrollConnections(patch) {
        const { payrollConnections } = this.state;
        this.setState({ payrollConnections: { ...(payrollConnections || {}), ...patch } });
    }

    /**
     * Is this connection's panel open?
     * @param {string | null} connectionKey
     * @returns {boolean}
     */
    isConnectionExpanded(connectionKey) {
        if (!connectionKey) {
            return false;
        }
        return (this.state.expandedConnections || []).includes(connectionKey);
    }

    /**
     * Opens or closes one connection's panel.
     *
     * A list, not a boolean: two jobs mean two panels, and an admin comparing
     * them has both open. Writes nothing when the state already matches, so a
     * re-render cannot loop through here.
     * @param {string} connectionKey
     * @param {boolean} expanded
     */
    setConnectionExpanded(connectionKey, expanded) {
        if (!connectionKey) {
            return;
        }
        const current = this.state.expandedConnections || [];
        if (current.includes(connectionKey) === !!expanded) {
            return;
        }
        this.setState({
            expandedConnections: expanded
                ? [...current, connectionKey]
                : current.filter((key) => key !== connectionKey),
        });
    }

    /**
     * Shallow-merges one connection's events slice, leaving every other
     * connection untouched — expanding a second job must never disturb the
     * first one's loaded history.
     * @param {string} connectionKey
     * @param {Object} patch
     */
    setConnectionEventsSlice(connectionKey, patch) {
        const { connectionEvents } = this.state;
        const current = connectionEvents || {};
        this.setState({
            connectionEvents: {
                ...current,
                [connectionKey]: { ...(current[connectionKey] || {}), ...patch },
            },
        });
    }

    /**
     * Appends a fetched page to a connection's events.
     *
     * `items` is concatenated rather than replaced so "Show more" grows the
     * list, and both error fields are cleared because a successful page means
     * the slice is readable again — including `moreError`, which a previous
     * failed page may have left set.
     * @param {string} connectionKey
     * @param {Array<Object>} items
     * @param {Object | null} pageinfo
     */
    appendConnectionEvents(connectionKey, items, pageinfo) {
        const { connectionEvents } = this.state;
        const current = connectionEvents || {};
        const existing = current[connectionKey] || {};
        this.setState({
            connectionEvents: {
                ...current,
                [connectionKey]: {
                    ...existing,
                    items: [...(existing.items || []), ...(items || [])],
                    pageinfo: pageinfo || existing.pageinfo || null,
                    loading: false,
                    loadingMore: false,
                    error: null,
                    moreError: null,
                },
            },
        });
    }

    /**
     * Drops a connection's loaded events entirely, so the next expand refetches.
     *
     * Used after the two sync admin actions: they mutate `sync` rows, which
     * changes the very events this slice is caching.
     *
     * Bumps the generation *before* the early return: a first page can be in
     * flight without having written anything readable back yet, and that
     * request has to be invalidated too.
     * @param {string} connectionKey
     */
    clearConnectionEvents(connectionKey) {
        this.bumpConnectionEventsGeneration(connectionKey);

        const { connectionEvents } = this.state;
        const current = connectionEvents || {};
        if (!(connectionKey in current)) {
            return;
        }
        const next = { ...current };
        delete next[connectionKey];
        this.setState({ connectionEvents: next });
    }

    /**
     * Invalidates every events request in flight for one connection.
     *
     * Generation tokens rather than state flags, mirroring
     * `reports.controller.js`'s per-`clientId` counter: a request that was
     * issued before an admin action mutated the underlying `sync` row is
     * carrying pre-mutation events, and landing it would both show the admin
     * history that no longer exists and satisfy the fetch-once guard in
     * `loadConnectionEvents`, so no later expand would ever correct it.
     *
     * Lives outside React state on purpose — it is read inside a promise
     * continuation, where a batched `setState` may not have flushed yet.
     * @param {string} connectionKey
     * @returns {number} The generation now current for that connection.
     */
    bumpConnectionEventsGeneration(connectionKey) {
        if (!this._connectionEventsGeneration) this._connectionEventsGeneration = {};
        const next = (this._connectionEventsGeneration[connectionKey] || 0) + 1;
        this._connectionEventsGeneration[connectionKey] = next;
        return next;
    }

    /**
     * Is `generation` still the current one for this connection?
     * @param {string} connectionKey
     * @param {number} generation
     * @returns {boolean}
     */
    isCurrentConnectionEventsGeneration(connectionKey, generation) {
        return (this._connectionEventsGeneration?.[connectionKey] || 0) === generation;
    }

    /**
     * Same token scheme for the connection summary, which has its own races:
     * the panel's Retry and an admin action's refresh can both be in flight,
     * and the slower one must not overwrite the newer answer.
     * @returns {number}
     */
    bumpPayrollConnectionsGeneration() {
        this._payrollConnectionsGeneration = (this._payrollConnectionsGeneration || 0) + 1;
        return this._payrollConnectionsGeneration;
    }

    /**
     * @param {number} generation
     * @returns {boolean}
     */
    isCurrentPayrollConnectionsGeneration(generation) {
        return (this._payrollConnectionsGeneration || 0) === generation;
    }
}

export default UserDelegate;
